import { DataType, FieldValueType, type IInfoGroup, type IInfoGroupField, type ValueType } from '@inpart-io/padme-iap';
import { computed, type ComputedRef } from 'vue';

import { useWorkflowFiltersHelper } from '@/components/lead-list/components/filters/composables/workflowFiltersHelper';
import { ICONS_NAME } from '@/constants/ui/icons.constants';
import { isInfogroupFieldEnabled } from '@/services/infogroups-service';

export function useInfoGroupsHelper(): {
    canUseLayout: Readonly<ComputedRef<boolean>>;
    containsNoneTextFields: (infoGroup: IInfoGroup) => boolean;
    getSortedInfoGroupFields: (infoGroupFields: IInfoGroupField[], includeAll?: boolean, useAlphabeticalOrder?: boolean) => IInfoGroupField[];
    getIconForInfoGroupField: (infoGroupField: IInfoGroupField) => string;
    getActiveInfoGroupsProperties: (infoGroupList: IInfoGroup[]) => string;
    getShortenActiveInfoGroupFieldProperties: (infoGroupField: IInfoGroup) => string;
    isLayoutVue: (dataType: DataType) => boolean;
} {
    const fieldIconsMap = new Map<ValueType, string>([
        [FieldValueType.BOOLEAN, ICONS_NAME.LIST_VALUE],
        [FieldValueType.TEXT, ICONS_NAME.SHORT_TEXT],
        [FieldValueType.LONG_TEXT, ICONS_NAME.LONG_TEXT],
        [FieldValueType.LIST_VALUE, ICONS_NAME.LIST_VALUE],
        [FieldValueType.DATE, ICONS_NAME.CALENDAR_OUTLINE],
        [FieldValueType.NUMBER, ICONS_NAME.NUMBER]
    ]);

    const { useDealWorkflow } = useWorkflowFiltersHelper();

    const canUseLayout = computed<boolean>(() => {
        return useDealWorkflow.value;
    });

    const isLayoutVue = (dataType: DataType): boolean => {
        return canUseLayout.value && dataType !== DataType.AgreementDetail && dataType !== DataType.AllianceDetail;
    };

    const containsNoneTextFields = (infoGroup: IInfoGroup): boolean => {
        const infoGroupsField = infoGroup.fields.find((field: IInfoGroupField) => {
            return field.valueType !== FieldValueType.LONG_TEXT && field.valueType !== FieldValueType.TEXT && isInfogroupFieldEnabled(field);
        });

        return infoGroupsField !== undefined && infoGroupsField !== null;
    };

    const getSortedInfoGroupFields = (infoGroupFields: IInfoGroupField[], includeAll = true, useAlphabeticalOrder = false): IInfoGroupField[] => {
        let existingInfoGroupFields = infoGroupFields.filter((infoGroupField: IInfoGroupField) => {
            return isInfogroupFieldEnabled(infoGroupField);
        });
        if (useAlphabeticalOrder) {
            existingInfoGroupFields = existingInfoGroupFields.sort((infoGroupField1: IInfoGroupField, infoGroupField2: IInfoGroupField) => {
                return infoGroupField1.defaultLabel.localeCompare(infoGroupField2.defaultLabel);
            });
        }

        let nonExistingInfoGroupFields: IInfoGroupField[] = [];
        if (includeAll) {
            nonExistingInfoGroupFields = infoGroupFields.filter((infoGroupField: IInfoGroupField) => {
                return !isInfogroupFieldEnabled(infoGroupField);
            });
            if (useAlphabeticalOrder) {
                nonExistingInfoGroupFields = nonExistingInfoGroupFields.sort((infoGroupField1: IInfoGroupField, infoGroupField2: IInfoGroupField) => {
                    return infoGroupField1.defaultLabel.localeCompare(infoGroupField2.defaultLabel);
                });
            }
        }
        return [...existingInfoGroupFields, ...nonExistingInfoGroupFields];
    };

    const getIconForInfoGroupField = (infoGroupField: IInfoGroupField): string => {
        return fieldIconsMap.get(infoGroupField.valueType);
    };

    const getActiveInfoGroupFieldProperties = (ig: IInfoGroup): string => {
        return ig.fields
            .filter((field: IInfoGroupField) => {
                return isInfogroupFieldEnabled(field);
            })
            .map((field: IInfoGroupField) => {
                return field.valueType === FieldValueType.LIST_VALUE ? `${field.attributeName}.label` : field.attributeName;
            })
            .toString();
    };

    const getShortenActiveInfoGroupFieldProperties = (ig: IInfoGroup): string => {
        const listFields: string[] = ig.fields
            .filter((field: IInfoGroupField) => {
                return isInfogroupFieldEnabled(field) && field.valueType === FieldValueType.LIST_VALUE;
            })
            .map((field: IInfoGroupField) => {
                return `${field.attributeName}.label`;
            });

        const listFieldsString: string = listFields.length > 0 ? `,${listFields.join(',')}` : '';
        return `${ig.attributeName}.[*${listFieldsString}]`;
    };

    const getActiveInfoGroupsProperties = (igList: IInfoGroup[]): string => {
        return igList.length > 0
            ? `infogroups.[${igList
                  .map((activeInfoGroup: IInfoGroup) => {
                      const attribute = activeInfoGroup.attributeName;

                      return `${attribute}.[${getActiveInfoGroupFieldProperties(activeInfoGroup)}]`;
                  })
                  .toString()}]`
            : '';
    };

    return {
        containsNoneTextFields,
        getSortedInfoGroupFields,
        getIconForInfoGroupField,
        getActiveInfoGroupsProperties,
        getShortenActiveInfoGroupFieldProperties,
        canUseLayout,
        isLayoutVue
    };
}
